/**
 * Federal tax tables by year. Published years carry their IRS source; later years are estimated by
 * indexing the latest published year for inflation (as the IRS does each fall) and are marked
 * estimated wherever they're shown. Review every year in Q4 when the IRS publishes the next year.
 * Amounts are whole dollars.
 */
export type FilingStatus = "single" | "mfj" | "mfs" | "hoh";
export const FILING_STATUSES: FilingStatus[] = ["single", "mfj", "mfs", "hoh"];
export const FILING_STATUS_NAMES: Record<FilingStatus, string> = { single: "Single", mfj: "Married filing jointly", mfs: "Married filing separately", hoh: "Head of household" };

type ByStatus = Record<FilingStatus, number>;

export type YearTable = {
  year: number;
  estimated: boolean;
  sources: string[];
  /** Upper bounds of the 10, 12, 22, 24, 32 and 35% brackets (37% above). */
  brackets: Record<FilingStatus, number[]>;
  standard: ByStatus;
  additional65: { married: number; unmarried: number };
  /** [top of 0%, top of 15%] for long-term gains and qualified dividends. */
  capGains: Record<FilingStatus, [number, number]>;
  niitThreshold: ByStatus;
  addlMedicareThreshold: ByStatus;
  amt: { exemption: ByStatus; phaseStart: ByStatus; phaseRate: number; rate28Over: number };
  ssWageBase: number;
  /** Federal unemployment (FUTA): wages taxed per employee per year, and the net employer rate after the full state credit (IRC 3301, 3302). */
  futaWageBase: number;
  futaNetRate: number;
  qbi: { threshold: ByStatus; range: ByStatus; minimum?: { amount: number; activeQbiAtLeast: number } };
  salt: { cap: ByStatus; phaseStart: ByStatus; rate: number; floor: ByStatus };
  ctc: { perChild: number; refundable: number; otherDependent: number; phaseStart: ByStatus };
  senior?: { amount: number; phaseStart: ByStatus; rate: number; years: { from: number; to: number } };
  /** Reduced by `perThousand` for each full $1,000 of MAGI over `phaseStart` (Schedule 1-A lines 11 and 19). */
  tips?: { cap: number; phaseStart: ByStatus; perThousand: number; years: { from: number; to: number } };
  overtime?: { cap: ByStatus; phaseStart: ByStatus; perThousand: number; years: { from: number; to: number } };
  /** From 2026: non-itemizers deduct cash gifts up to this; itemizers lose the first 0.5% of AGI. */
  charitableNonItemizer?: ByStatus;
  charitableFloorPct?: number;
  /** From 2026: itemized deductions worth at most 35% to those in the 37% bracket. */
  itemizedLimit37?: boolean;
  limits: { k401: number; k401CatchUp: number; k401SuperCatchUp: number; ira: number; iraCatchUp: number; sepMax: number; qcd: number };
};

const s4 = (single: number, mfj: number, mfs: number, hoh: number): ByStatus => ({ single, mfj, mfs, hoh });
const OBBBA = "One Big Beautiful Bill Act, P.L. 119-21 (July 4, 2025)";
const senior = { amount: 6_000, phaseStart: s4(75_000, 150_000, 75_000, 75_000), rate: 0.06, years: { from: 2025, to: 2028 } };
const tips = { cap: 25_000, phaseStart: s4(150_000, 300_000, 150_000, 150_000), perThousand: 100, years: { from: 2025, to: 2028 } };
const overtime = { cap: s4(12_500, 25_000, 12_500, 12_500), phaseStart: s4(150_000, 300_000, 150_000, 150_000), perThousand: 100, years: { from: 2025, to: 2028 } };

export const TABLES: Record<number, YearTable> = {
  2025: {
    year: 2025,
    estimated: false,
    sources: ["IRS Rev. Proc. 2024-40", OBBBA, "SSA 2025 COLA fact sheet", "IRS Notice 2024-80"],
    brackets: {
      single: [11_925, 48_475, 103_350, 197_300, 250_525, 626_350],
      mfj: [23_850, 96_950, 206_700, 394_600, 501_050, 751_600],
      mfs: [11_925, 48_475, 103_350, 197_300, 250_525, 375_800],
      hoh: [17_000, 64_850, 103_350, 197_300, 250_500, 626_350],
    },
    standard: s4(15_750, 31_500, 15_750, 23_625),
    additional65: { married: 1_600, unmarried: 2_000 },
    capGains: { single: [48_350, 533_400], mfj: [96_700, 600_050], mfs: [48_350, 300_000], hoh: [64_750, 566_700] },
    niitThreshold: s4(200_000, 250_000, 125_000, 200_000),
    addlMedicareThreshold: s4(200_000, 250_000, 125_000, 200_000),
    amt: { exemption: s4(88_100, 137_000, 68_500, 88_100), phaseStart: s4(626_350, 1_252_700, 626_350, 626_350), phaseRate: 0.25, rate28Over: 239_100 },
    ssWageBase: 176_100,
    futaWageBase: 7_000,
    futaNetRate: 0.006,
    qbi: { threshold: s4(197_300, 394_600, 197_300, 197_300), range: s4(50_000, 100_000, 50_000, 50_000) },
    salt: { cap: s4(40_000, 40_000, 20_000, 40_000), phaseStart: s4(500_000, 500_000, 250_000, 500_000), rate: 0.3, floor: s4(10_000, 10_000, 5_000, 10_000) },
    ctc: { perChild: 2_200, refundable: 1_700, otherDependent: 500, phaseStart: s4(200_000, 400_000, 200_000, 200_000) },
    senior,
    tips,
    overtime,
    limits: { k401: 23_500, k401CatchUp: 7_500, k401SuperCatchUp: 11_250, ira: 7_000, iraCatchUp: 1_000, sepMax: 70_000, qcd: 108_000 },
  },
  2026: {
    year: 2026,
    estimated: false,
    sources: ["IRS Rev. Proc. 2025-32", OBBBA, "SSA 2026 COLA fact sheet", "IRS Notice 2025-67"],
    brackets: {
      single: [12_400, 50_400, 105_700, 201_775, 256_225, 640_600],
      mfj: [24_800, 100_800, 211_400, 403_550, 512_450, 768_700],
      mfs: [12_400, 50_400, 105_700, 201_775, 256_225, 384_350],
      hoh: [17_700, 67_450, 105_700, 201_750, 256_200, 640_600],
    },
    standard: s4(16_100, 32_200, 16_100, 24_150),
    additional65: { married: 1_650, unmarried: 2_050 },
    capGains: { single: [49_450, 545_500], mfj: [98_900, 613_700], mfs: [49_450, 306_850], hoh: [66_200, 579_600] },
    niitThreshold: s4(200_000, 250_000, 125_000, 200_000),
    addlMedicareThreshold: s4(200_000, 250_000, 125_000, 200_000),
    amt: { exemption: s4(90_100, 140_200, 70_100, 90_100), phaseStart: s4(500_000, 1_000_000, 500_000, 500_000), phaseRate: 0.5, rate28Over: 244_500 },
    ssWageBase: 184_500,
    futaWageBase: 7_000,
    futaNetRate: 0.006,
    qbi: { threshold: s4(201_750, 403_500, 201_775, 201_750), range: s4(75_000, 150_000, 75_000, 75_000), minimum: { amount: 400, activeQbiAtLeast: 1_000 } },
    salt: { cap: s4(40_400, 40_400, 20_200, 40_400), phaseStart: s4(505_000, 505_000, 252_500, 505_000), rate: 0.3, floor: s4(10_000, 10_000, 5_000, 10_000) },
    ctc: { perChild: 2_200, refundable: 1_700, otherDependent: 500, phaseStart: s4(200_000, 400_000, 200_000, 200_000) },
    senior,
    tips,
    overtime,
    charitableNonItemizer: s4(1_000, 2_000, 1_000, 1_000),
    charitableFloorPct: 0.005,
    itemizedLimit37: true,
    limits: { k401: 24_500, k401CatchUp: 8_000, k401SuperCatchUp: 11_250, ira: 7_500, iraCatchUp: 1_100, sepMax: 72_000, qcd: 111_000 },
  },
};

/**
 * Social Security wage bases SSA has announced for years whose IRS tables aren't published yet.
 * 2027: $192,300 (SSA 2027 COLA fact sheet and contribution and benefit base table, announced
 * October 14, 2026; see docs/accuracy/tax-tables-verification-2026-10b.json). As of 2026-10-19 the
 * IRS had not published the 2027 inflation adjustments, so the rest of 2027 stays estimated.
 */
const KNOWN_WAGE_BASE: Record<number, { amount: number; source: string }> = { 2027: { amount: 192_300, source: "SSA 2027 COLA fact sheet (wage base)" } };

export const LATEST_PUBLISHED = Math.max(...Object.keys(TABLES).map(Number));

/**
 * True when the year's Social Security wage base is our estimate: neither the IRS tables nor an
 * SSA announcement for that year are in the code yet. Screens that rest on it must say so.
 */
export function ssWageBaseEstimated(year: number): boolean {
  return !TABLES[year] && !KNOWN_WAGE_BASE[year];
}

/** IRS-style indexing: multiply, then round down to the given multiple. */
const index = (v: number, f: number, step = 50) => Math.floor((v * f) / step) * step;
const indexAll = (b: ByStatus, f: number, step = 50): ByStatus => s4(index(b.single, f, step), index(b.mfj, f, step), index(b.mfs, f, step), index(b.hoh, f, step));

/** The table for a year: published, or estimated from the latest published year at `inflationPct`. */
export function tableFor(year: number, inflationPct = 2.5): YearTable {
  if (TABLES[year]) return TABLES[year]!;
  if (year < 2025) throw new Error(`Tax tables before 2025 are not available (asked for ${year})`);
  const base = TABLES[LATEST_PUBLISHED]!;
  const f = Math.pow(1 + inflationPct / 100, year - LATEST_PUBLISHED);
  const brackets = Object.fromEntries(Object.entries(base.brackets).map(([k, v]) => [k, v.map((x) => index(x, f, 25))])) as Record<FilingStatus, number[]>;
  const capGains = Object.fromEntries(Object.entries(base.capGains).map(([k, [a, b]]) => [k, [index(a, f), index(b, f)]])) as Record<FilingStatus, [number, number]>;
  return {
    ...base,
    year,
    estimated: true,
    sources: [`Estimated from ${LATEST_PUBLISHED} indexed at ${inflationPct}% a year; the IRS publishes the actual figures each fall`, ...(KNOWN_WAGE_BASE[year] ? [KNOWN_WAGE_BASE[year]!.source] : [])],
    brackets,
    standard: indexAll(base.standard, f),
    additional65: { married: index(base.additional65.married, f), unmarried: index(base.additional65.unmarried, f) },
    capGains,
    // NIIT, Additional Medicare, the CTC phase-out and the SALT floor are fixed in law (not indexed).
    amt: { ...base.amt, exemption: indexAll(base.amt.exemption, f), phaseStart: indexAll(base.amt.phaseStart, f), rate28Over: index(base.amt.rate28Over, f, 100) },
    // SSA publishes the wage base each October; use it once known.
    ssWageBase: KNOWN_WAGE_BASE[year]?.amount ?? index(base.ssWageBase, f, 300),
    qbi: { ...base.qbi, threshold: indexAll(base.qbi.threshold, f) },
    // The SALT cap rises 1% a year through 2029 and returns to $10,000 in 2030.
    salt:
      year >= 2030
        ? { cap: s4(10_000, 10_000, 5_000, 10_000), phaseStart: s4(Infinity, Infinity, Infinity, Infinity), rate: 0, floor: s4(10_000, 10_000, 5_000, 10_000) }
        : { ...base.salt, cap: indexAll(base.salt.cap, Math.pow(1.01, year - LATEST_PUBLISHED), 1), phaseStart: indexAll(base.salt.phaseStart, Math.pow(1.01, year - LATEST_PUBLISHED), 1) },
    ctc: { ...base.ctc, perChild: index(base.ctc.perChild, f, 100), refundable: index(base.ctc.refundable, f, 100) },
    limits: { ...base.limits, k401: index(base.limits.k401, f, 500), ira: index(base.limits.ira, f, 500), sepMax: index(base.limits.sepMax, f, 1000), qcd: index(base.limits.qcd, f, 1000) },
  };
}
